import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant, ApiError } from "../lib/api"

type RequestStatus = "pending" | "approved" | "rejected" | "cancelled" | "consumed"

interface LeaveType {
  id: string
  code: string
  name: string
  paid: boolean
  accrualDaysPerYear: string
}

interface Employee {
  id: string
  employeeNo: string
  fullName: string
}

interface LeaveRequest {
  id: string
  employeeId: string
  leaveTypeId: string
  fromDate: string
  toDate: string
  days: string
  reason: string | null
  status: RequestStatus
  decisionNote: string | null
  decidedAt: string | null
  leaveType: { code: string; name: string; paid: boolean }
  employee: { fullName: string; employeeNo: string }
}

interface LeaveBalance {
  id: string
  employeeId: string
  leaveTypeId: string
  year: number
  entitledDays: string
  usedDays: string
  pendingDays: string
  carriedOverDays: string
  leaveType: { code: string; name: string; paid: boolean }
  employee: { fullName: string; employeeNo: string }
}

const STATUS_PILL: Record<RequestStatus, string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-600",
  consumed: "bg-blue-100 text-blue-700",
}

export function LeavePage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const [tab, setTab] = useState<"requests" | "balances">("requests")
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "">("pending")
  const [managerFilter, setManagerFilter] = useState<string>("")
  const [showSubmit, setShowSubmit] = useState(false)

  const requests = useQuery({
    queryKey: ["leave-requests", slug, statusFilter, managerFilter],
    enabled: !!slug,
    queryFn: () => {
      const params = new URLSearchParams()
      if (statusFilter) params.set("status", statusFilter)
      if (managerFilter) params.set("managerEmployeeId", managerFilter)
      const qs = params.toString()
      return apiTenant<{ requests: LeaveRequest[] }>(slug, `/leave/requests${qs ? `?${qs}` : ""}`)
    },
  })

  const balances = useQuery({
    queryKey: ["leave-balances", slug],
    enabled: !!slug && tab === "balances",
    queryFn: () => apiTenant<{ balances: LeaveBalance[] }>(slug, `/leave/balances?year=${new Date().getFullYear()}`),
  })

  // Active employees — drives both the manager dropdown and the
  // submit-request modal.
  const employees = useQuery({
    queryKey: ["employees-active", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ employees: Employee[] }>(slug, "/employees?status=active"),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["leave-requests", slug] })
    qc.invalidateQueries({ queryKey: ["leave-balances", slug] })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Leave</h1>
          <p className="text-xs text-slate-500 mt-1">
            Tenant <span className="font-mono">{slug}</span>
          </p>
        </div>
        <button
          onClick={() => setShowSubmit(true)}
          className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded"
        >
          + Submit request
        </button>
      </div>

      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          <TabButton active={tab === "requests"} onClick={() => setTab("requests")}>
            Requests
          </TabButton>
          <TabButton active={tab === "balances"} onClick={() => setTab("balances")}>
            Balances ({new Date().getFullYear()})
          </TabButton>
        </div>
      </div>

      {tab === "requests" && (
        <RequestsTab
          slug={slug}
          requests={requests.data?.requests ?? []}
          employees={employees.data?.employees ?? []}
          loading={requests.isLoading}
          error={requests.error}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          managerFilter={managerFilter}
          setManagerFilter={setManagerFilter}
          onAction={invalidate}
        />
      )}

      {tab === "balances" && (
        <BalancesTab
          balances={balances.data?.balances ?? []}
          loading={balances.isLoading}
          error={balances.error}
        />
      )}

      {showSubmit && (
        <SubmitRequestModal
          slug={slug}
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => {
            invalidate()
            setShowSubmit(false)
          }}
        />
      )}
    </div>
  )
}

function TabButton(props: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={props.onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
        props.active ? "border-brand-500 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {props.children}
    </button>
  )
}

// ────────────────────────────────────────────────────────────
// Requests tab
// ────────────────────────────────────────────────────────────

function RequestsTab(props: {
  slug: string
  requests: LeaveRequest[]
  employees: Employee[]
  loading: boolean
  error: Error | null
  statusFilter: RequestStatus | ""
  setStatusFilter: (s: RequestStatus | "") => void
  managerFilter: string
  setManagerFilter: (s: string) => void
  onAction: () => void
}) {
  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="font-medium">Leave requests</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500">Manager</label>
          <select
            value={props.managerFilter}
            onChange={(e) => props.setManagerFilter(e.target.value)}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
            title="Show leave for this manager's direct reports"
          >
            <option value="">— anyone —</option>
            {props.employees.map((e) => (
              <option key={e.id} value={e.id}>{e.employeeNo} · {e.fullName}</option>
            ))}
          </select>
          <select
            value={props.statusFilter}
            onChange={(e) => props.setStatusFilter(e.target.value as RequestStatus | "")}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
            <option value="consumed">Consumed</option>
          </select>
        </div>
      </div>

      {props.loading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {props.error && <div className="p-6 text-sm text-red-600">{(props.error as Error).message}</div>}

      {!props.loading && props.requests.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">
          No {props.statusFilter || "leave"} requests.
        </div>
      )}

      {props.requests.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Employee</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Dates</th>
              <th className="text-right px-4 py-2 font-medium">Days</th>
              <th className="text-left px-4 py-2 font-medium">Reason</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {props.requests.map((r) => (
              <RequestRow key={r.id} slug={props.slug} request={r} onAction={props.onAction} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function RequestRow(props: { slug: string; request: LeaveRequest; onAction: () => void }) {
  const r = props.request
  const decide = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      apiTenant(props.slug, `/leave/requests/${r.id}/decide`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      }),
    onSuccess: props.onAction,
  })
  const cancel = useMutation({
    mutationFn: () => apiTenant(props.slug, `/leave/requests/${r.id}/cancel`, { method: "POST" }),
    onSuccess: props.onAction,
  })

  return (
    <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
      <td className="px-4 py-2">
        <div className="font-medium">{r.employee.fullName}</div>
        <div className="text-xs text-slate-500 font-mono">{r.employee.employeeNo}</div>
      </td>
      <td className="px-4 py-2">
        <div className="font-mono text-xs">{r.leaveType.code}</div>
        <div className="text-xs text-slate-500">{r.leaveType.paid ? "paid" : "unpaid"}</div>
      </td>
      <td className="px-4 py-2 font-mono text-xs">
        {r.fromDate.slice(0, 10)} → {r.toDate.slice(0, 10)}
      </td>
      <td className="px-4 py-2 text-right font-mono">{Number(r.days).toFixed(1)}</td>
      <td className="px-4 py-2 text-xs text-slate-600 max-w-[200px] truncate" title={r.reason ?? ""}>
        {r.reason ?? "—"}
      </td>
      <td className="px-4 py-2">
        <span className={`inline-block px-2 py-0.5 text-xs rounded ${STATUS_PILL[r.status]}`}>{r.status}</span>
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {r.status === "pending" && (
          <>
            <button
              onClick={() => decide.mutate("approve")}
              disabled={decide.isPending}
              className="text-xs text-emerald-700 hover:text-emerald-900 mr-3"
            >
              Approve
            </button>
            <button
              onClick={() => decide.mutate("reject")}
              disabled={decide.isPending}
              className="text-xs text-red-600 hover:text-red-800 mr-3"
            >
              Reject
            </button>
          </>
        )}
        {(r.status === "pending" || r.status === "approved") && (
          <button
            onClick={() => {
              if (window.confirm(`Cancel this leave request? Days will refund to the balance.`)) cancel.mutate()
            }}
            disabled={cancel.isPending}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        )}
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────
// Balances tab
// ────────────────────────────────────────────────────────────

function BalancesTab(props: { balances: LeaveBalance[]; loading: boolean; error: Error | null }) {
  // Group by employee for readability.
  const byEmployee = new Map<string, LeaveBalance[]>()
  for (const b of props.balances) {
    const arr = byEmployee.get(b.employeeId) ?? []
    arr.push(b)
    byEmployee.set(b.employeeId, arr)
  }

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 font-medium">Leave balances</div>
      {props.loading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {props.error && <div className="p-6 text-sm text-red-600">{(props.error as Error).message}</div>}
      {!props.loading && props.balances.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">
          No balances yet — they're created lazily on the first leave request of the year.
        </div>
      )}
      {byEmployee.size > 0 && (
        <div className="divide-y divide-slate-100">
          {Array.from(byEmployee.entries()).map(([eid, balances]) => (
            <div key={eid} className="px-4 py-3">
              <div className="text-sm font-medium mb-2">
                {balances[0].employee.fullName}{" "}
                <span className="text-xs text-slate-500 font-mono ml-2">{balances[0].employee.employeeNo}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {balances.map((b) => {
                  const remaining = Number(b.entitledDays) + Number(b.carriedOverDays) - Number(b.usedDays)
                  return (
                    <div key={b.id} className="bg-slate-50 border border-slate-200 rounded p-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">{b.leaveType.code}</div>
                      <div className="text-base font-mono font-semibold">{remaining.toFixed(1)}</div>
                      <div className="text-[11px] text-slate-500">
                        {Number(b.usedDays).toFixed(1)} used
                        {Number(b.pendingDays) > 0 && <> · {Number(b.pendingDays).toFixed(1)} pending</>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Submit request modal
// ────────────────────────────────────────────────────────────

function SubmitRequestModal(props: { slug: string; onClose: () => void; onSubmitted: () => void }) {
  const [employeeId, setEmployeeId] = useState("")
  const [leaveTypeId, setLeaveTypeId] = useState("")
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10))
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10))
  const [reason, setReason] = useState("")
  const [error, setError] = useState("")

  const employees = useQuery({
    queryKey: ["employees-active", props.slug],
    queryFn: () => apiTenant<{ employees: Employee[] }>(props.slug, "/employees?status=active"),
  })
  const leaveTypes = useQuery({
    queryKey: ["leave-types", props.slug],
    queryFn: () => apiTenant<{ leaveTypes: LeaveType[] }>(props.slug, "/leave-types"),
  })

  const submit = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, "/leave/requests", {
        method: "POST",
        body: JSON.stringify({
          employeeId,
          leaveTypeId,
          fromDate,
          toDate,
          reason: reason.trim() || null,
        }),
      }),
    onSuccess: props.onSubmitted,
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.message === "to_before_from") setError("End date is before start date.")
        else if (err.message === "zero_day_request") setError("Request must cover at least one day.")
        else setError(err.message)
      } else setError(err instanceof Error ? err.message : "Failed")
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!employeeId || !leaveTypeId) {
      setError("Pick an employee and a leave type.")
      return
    }
    submit.mutate()
  }

  // Inclusive day count for the modal so the user sees what they'll consume.
  function inclusiveDays(): number {
    if (!fromDate || !toDate) return 0
    const a = new Date(fromDate)
    const b = new Date(toDate)
    if (b < a) return 0
    return Math.floor((b.getTime() - a.getTime()) / 86_400_000) + 1
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg w-full max-w-lg shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">Submit leave request</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee *">
              <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="form-input">
                <option value="">—</option>
                {employees.data?.employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.employeeNo} · {e.fullName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Leave type *">
              <select value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)} className="form-input">
                <option value="">—</option>
                {leaveTypes.data?.leaveTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} ({t.paid ? "paid" : "unpaid"})
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From *">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-input" />
            </Field>
            <Field label="To *">
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-input" />
            </Field>
          </div>
          <div className="text-xs text-slate-500">
            Days: <strong>{inclusiveDays()}</strong> (inclusive)
          </div>
          <Field label="Reason">
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="form-input"
              placeholder="Optional context for the approver"
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submit.isPending}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
          >
            {submit.isPending ? "Submitting…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}
